import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

// Context
import { CoursesNewContext, CoursesNewContextType } from "./context";

// Theme
import ThemeProvider from "../../../utils/theme";

// Steps
import BasicInfo from "./Steps/BasicInfo";
import EducationLevel from "./Steps/EducationLevel";
import Schedule from "./Steps/Schedule";
import Sources from "./Steps/Sources";
import Materials from "./Steps/Materials";
import Structure from "./Steps/Structure";
import Finished from "./Finished";

// Components
import Tracking from "./Tracking";
import ProcessingModal from "../../../components/ProcessingModal";
import Spinner from "../../../components/spinner";

// Styles
import styles from "./style.module.scss";

// Utils
import { coursesUrl, courseUrl, noteUrl } from '../../../utils/urls'
import useCsrfToken from "../../../utils/hooks/csfrToken";

// Types
import { CourseData } from "./types";
import { getSteps } from "./utils";

interface CoursesNewProps {
  note_id: string;
}

const CoursesNew: React.FC<CoursesNewProps> = ({ note_id }) => {
  // Translations
  const { t } = useTranslation();
  const csrfToken = useCsrfToken();

  // State
  const [course, setCourse] = useState<CourseData | null>(null);
  const [step, setStep] = useState<string>('basic_info');
  const [isFinished, setIsFinished] = useState<boolean>(false);

  const [processing, setProcessing] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  // Memoized Steps
  const steps = useMemo(() => {
    return getSteps(!!note_id);
  }, [note_id]);

  // Methods
  const createCourse = useCallback(async (courseData: CourseData) => {
    setProcessing(true);
    try {
      const formData = new FormData();
      formData.append('course[title]', courseData.title || '');
      formData.append('course[description]', courseData.description || '');
      formData.append('course[education_level]', courseData.education_level || '');
      formData.append('course[school_name]', courseData.school_name || '');
      formData.append('course[start_date]', courseData.start_date || '');
      formData.append('course[end_date]', courseData.end_date || '');
      if (courseData.files) {
        courseData.files.forEach((file) => {
          formData.append('course[files][]', file);
        });
      }
      if (courseData.links) {
        courseData.links.forEach((link) => {
          formData.append('course[links][]', link);
        });
      }

      const options = {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: formData,
      };

      const response = await fetch(coursesUrl(), options);
      if (response.ok) {
        const _course = await response.json();
        return { data: _course, success: true };
      }
      return { data: null, success: false };
    } catch (error) {
      console.error(error);
      return { data: null, success: false };
    } finally {
      setProcessing(false);
    }
  }, [csrfToken]);

  const updateCourse = useCallback(async (courseData: CourseData) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('course[title]', courseData.title || '');
      formData.append('course[description]', courseData.description || '');
      formData.append('course[education_level]', courseData.education_level || '');
      formData.append('course[school_name]', courseData.school_name || '');
      formData.append('course[start_date]', courseData.start_date || '');
      formData.append('course[end_date]', courseData.end_date || '');
      if (courseData.files) {
        courseData.files.forEach((file) => {
          formData.append('course[files][]', file);
        });
      }
      if (courseData.links) {
        courseData.links.forEach((link) => {
          formData.append('course[links][]', link);
        });
      }

      const options = {
        method: 'PATCH',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: formData,
      };

      const response = await fetch(courseUrl(courseData.id || 'unknown_course_id'), options);
      if (response.ok) {
        const _course = await response.json();
        return { data: _course, success: true };
      }
      return { data: null, success: false };
    } catch (error) {
      console.error(error);
      return { data: null, success: false };
    } finally {
      setLoading(false);
    }
  }, [csrfToken]);

  const loadAssociatedNote = useCallback(async () => {
    setLoading(true);
    try {

      const response = await fetch(noteUrl(note_id), {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken
        }
      });
      if (response.ok) {
        const _note = await response.json();
        const note = _note.data.attributes;
        setCourse({ ...course, title: note.title, description: '', mainNote: note });
        setStep('education_level');
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [note_id, csrfToken]);

  useEffect(() => {
    if (note_id) loadAssociatedNote();
  }, [note_id]);

  // Handlers
  const handleNextStep = useCallback(async (data: CourseData | null) => {
    const nextStep = steps[steps.indexOf(step) + 1];

    if (nextStep) {
      setCourse({ ...course, ...data });
      setStep(nextStep);
    } else if (isLastStep) {
      handleFinishCourse();
    }
  }, [step]);

  const handlePrevStep = useCallback(() => {
    const prevStep = steps[steps.indexOf(step) - 1];
    if (prevStep) {
      setStep(prevStep);
    }
  }, [step]);

  const handleFinishCourse = useCallback(() => {
    setIsFinished(true);
  }, []);

  // Render
  const renderStep = useCallback(() => {
    switch (step) {
      case 'basic_info':
        return <BasicInfo onNext={handleNextStep} />;
      case 'education_level':
        return <EducationLevel onNext={handleNextStep} onPrev={handlePrevStep} />;
      case 'schedule':
        return <Schedule onNext={handleNextStep} onPrev={handlePrevStep} />;
      case 'sources':
        return <Sources onNext={handleNextStep} onPrev={handlePrevStep} />;
      case 'materials':
        return <Materials onNext={handleNextStep} onPrev={handlePrevStep} />;
      case 'structure':
        return <Structure onFinish={handleFinishCourse} onPrev={handlePrevStep} />;
      default:
        return null;
    }
  }, [step]);


  // Memoized
  const contextValue: CoursesNewContextType = useMemo(() => ({
    course,
    createCourse,
    updateCourse,
    processing,
  }), [course, createCourse, updateCourse, processing]);

  const isLastStep = useMemo(() => {
    return steps.indexOf(step) === steps.length - 1;
  }, [step]);

  // Main Render
  return (
    <ThemeProvider>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <CoursesNewContext.Provider value={contextValue}>

          {
            isFinished
              ? <Finished />
              : (
                <div className={styles.root}>
                  {loading && <Spinner />}

                  <Tracking currentStep={step} steps={steps} />

                  {renderStep()}
                </div>
              )
          }

          <ProcessingModal open={processing} />

        </CoursesNewContext.Provider >
      </LocalizationProvider>
    </ThemeProvider>
  );
};

export default CoursesNew;
